import type { Ref } from 'vue'
import type { SearchSuggestion, Supplier } from '~/types'
import type { CategoryTreeNode } from '~/stores/category-tree'
import { normalizeSlug } from '~/utils/slug'
import {
  findSupplierSearchMatch,
  rankSupplierMatches,
} from '~/utils/findSupplierSearchMatch'

function filterStatic(items: SearchSuggestion[], query: string): SearchSuggestion[] {
  if (!query) return []
  const lowerQuery = query.toLowerCase()
  return items.filter(item =>
    item.label?.toLowerCase().includes(lowerQuery)
    || (lowerQuery.length > 3 && item.value?.toLowerCase().includes(lowerQuery)),
  )
}

function toSupplierSuggestion(supplier: Pick<Supplier, 'name' | 'handle' | 'imageUrl' | 'image'>): SearchSuggestion {
  return {
    label: supplier.name,
    value: supplier.handle,
    type: 'supplier',
    url: `/supplier/${supplier.handle}`,
    imageUrl: supplier.imageUrl ?? supplier.image,
  }
}

function flattenTree(
  nodes: CategoryTreeNode[],
  locale: string,
  ancestors: CategoryTreeNode[] = [],
): SearchSuggestion[] {
  const result: SearchSuggestion[] = []
  for (const node of nodes) {
    const label = node.terms[locale] ?? ''
    const pathParts = [...ancestors, node].map(n => normalizeSlug(n.terms[locale] ?? ''))
    const url = `/c/${pathParts.join('/')}`
    const subtitleParts = [...ancestors, node].map(n => n.terms[locale] ?? '')
    const subtitle = subtitleParts.length > 1 ? subtitleParts.join(' > ') : undefined

    result.push({ label, value: node.id, type: 'category', url, subtitle })

    if (node.children.length > 0) {
      result.push(...flattenTree(node.children, locale, [...ancestors, node]))
    }
  }
  return result
}

export function useSearchSuggestions(searchQuery: Ref<string>) {
  const searchStore = useSearchStore()
  const supplierStore = useSupplier()
  const categoryStore = useCategoryGroup()
  const treeStore = useCategoryTreeStore()
  const { t, locale } = useI18n()
  const isLoading = computed(() => searchStore.isLoading)

  watch(() => categoryStore.categoryGroup, (groups) => {
    if (groups.length > 0) {
      categoryStore.fetchAllCategories()
    }
  }, { immediate: true })

  const treeCategorySuggestions = computed<SearchSuggestion[]>(() =>
    flattenTree(treeStore.roots, locale.value),
  )

  const legacyCategorySuggestions = computed<SearchSuggestion[]>(() =>
    categoryStore.categoryGroup.flatMap((group) => {
      const children = categoryStore.allCategoriesByGroup[group.handle] ?? []
      const parentUrl = normalizeSlug(t(group.i18nKey))

      return children
        .filter(cat => !cat.i18nKey.startsWith('enum.') || t(cat.i18nKey) !== cat.i18nKey)
        .map(cat => ({
          label: t(cat.i18nKey),
          value: cat.handle,
          type: 'category' as const,
          url: `/${parentUrl}/${normalizeSlug(t(cat.i18nKey))}`,
        }))
    }),
  )

  const categorySuggestions = computed<SearchSuggestion[]>(() =>
    treeStore.roots.length > 0
      ? treeCategorySuggestions.value
      : legacyCategorySuggestions.value,
  )

  function resolveMatchedSupplierSuggestion(query: string): SearchSuggestion | null {
    const match = findSupplierSearchMatch(query, supplierStore.suppliers)
    return match ? toSupplierSuggestion(match) : null
  }

  // dynamic suggestions based on search query
  const suggestionItems = computed<SearchSuggestion[]>(() => {
    const products: SearchSuggestion[] = (searchStore.data?.suggestions || []).map(s => ({
      label: s.label,
      value: s.value,
      type: 'product',
    }))

    if (!searchQuery.value) return products

    // Hide history-derived products until the autocomplete response for this
    // exact query lands, so the dropdown doesn't flash history before matches
    // arrive while the debounced request is still in flight (SHP-3781).
    const matchingProducts = searchStore.query === searchQuery.value ? products : []
    const matchingSuppliers = rankSupplierMatches(searchQuery.value, supplierStore.suppliers)
      .map(toSupplierSuggestion)
    const matchingCategories = filterStatic(categorySuggestions.value, searchQuery.value)
    return [...matchingSuppliers, ...matchingCategories, ...matchingProducts]
  })

  function resetSearchSuggestions() {
    searchStore.resetToHistory()
  }

  // Search Logic for autocomplete by delay 300ms
  const handleSearch = useDebounceFn((query: string) => {
    if (!query) {
      searchStore.resetToHistory()
      return
    }
    searchStore.autocomplete(query)
  }, 300)

  watch(searchQuery, q => handleSearch(q))

  return {
    suggestionItems,
    resolveMatchedSupplierSuggestion,
    resetSearchSuggestions,
    isLoading,
  }
}
